import { isValidElement, ReactNode, RefObject, useLayoutEffect } from 'react'

import { findLastFocusableNode } from 'hsds/utils/focus'

import type { UseModalAutoFocusProps } from './SimpleModal.types'

export function allowGlobalHotkeys(target: HTMLElement): boolean {
  if (target.dataset.blocksGlobalHotkeys) {
    return false
  }

  if (target.closest('[data-blocks-global-hotkeys="true"]')) {
    return false
  }

  return true
}

type ModalLayout = { unsetsModalDimensions?: boolean }

export function shouldUnsetDimensions(
  unsetDimensions: boolean | undefined,
  children: ReactNode
): boolean {
  if (unsetDimensions) return true
  if (!isValidElement(children)) return false

  const layout = children.type as unknown as ModalLayout

  return layout.unsetsModalDimensions === true
}

function getAutoFocusNode(
  modal: HTMLElement,
  focusTargetElement: RefObject<HTMLElement> | null
): HTMLElement {
  if (focusTargetElement?.current) {
    return focusTargetElement.current
  }

  const primaryFooterAction = modal.querySelector<HTMLElement>(
    '.SimpleModal__footer .c-Button.is-color-blue:last-child'
  )

  if (primaryFooterAction) {
    return primaryFooterAction
  }

  const anyPrimaryAction = modal.querySelector<HTMLElement>(
    '.c-Button.is-color-blue'
  )

  if (anyPrimaryAction) {
    return anyPrimaryAction
  }

  const lastFocusable = findLastFocusableNode(modal) as HTMLElement | null

  return lastFocusable ?? modal
}

/**
 * Moves focus into the modal once per open:
 *
 * - `focusTargetElement` when given
 * - otherwise the primary footer button, then any primary button
 * - otherwise the last focusable node, then the modal itself
 *
 * Buttons get the `is-focused` class so the focus ring shows; Button removes
 * it on blur.
 */
export const useModalAutoFocus = ({
  modalRef,
  shouldRender,
  show,
  focusTargetElement,
  focusModalOnShow,
}: UseModalAutoFocusProps): void => {
  // Reopening during the exit animation keeps shouldRender true the whole time,
  // so `show` is what tells the two opens apart.
  const isOpen = shouldRender && show

  useLayoutEffect(() => {
    const modal = modalRef.current

    if (!isOpen || !focusModalOnShow || !modal) return

    const node = getAutoFocusNode(modal, focusTargetElement)

    if (node.classList.contains('c-Button')) {
      node.classList.add('is-focused')
    }
    node.focus({ preventScroll: true })

    const modalBody = modal.querySelector('.SimpleModal__body')

    if (modalBody) {
      modalBody.scrollTop = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])
}

export function getFocusableElement(
  element: HTMLElement | null
): HTMLElement | null {
  if (!element) return null

  const selector =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  return element.querySelector(selector)
}

export function checkFocusWithin(element: HTMLElement): boolean {
  return element.contains(document.activeElement)
}
